import type { FunnelFightersAttribution } from './types'

const COOKIE_NAME = 'taka.ff.attribution'
const COOKIE_DOMAIN = '.taka.ai'
const COOKIE_MAX_AGE_DAYS = 30

const ATTRIBUTION_KEYS: Array<keyof FunnelFightersAttribution> = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'gclid',
  'ref',
]

const isValidStringValue = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

const isProductionHost = (): boolean =>
  typeof window !== 'undefined' && window.location.hostname.endsWith('taka.ai')

export const readAttributionCookie = (): FunnelFightersAttribution => {
  if (typeof document === 'undefined') return {}

  const cookieMatch = document.cookie.split('; ').find(row => row.startsWith(`${COOKIE_NAME}=`))

  if (!cookieMatch) return {}

  try {
    const raw = decodeURIComponent(cookieMatch.slice(COOKIE_NAME.length + 1))
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const result: FunnelFightersAttribution = {}
    for (const key of ATTRIBUTION_KEYS) {
      const value = parsed[key]
      if (isValidStringValue(value)) {
        result[key] = value
      }
    }
    return result
  } catch {
    return {}
  }
}

/**
 * Persist attribution params present in the current URL into the shared
 * `taka.ff.attribution` cookie. The marketing site captures these on the /invite
 * landing page, but `getAppUrl` also forwards them to `app.taka.ai`
 * (`.../login?ref=CODE`). Capturing here too means a referral survives even when
 * the parent-domain cookie didn't stick — Safari/ITP caps JS-set cookies well
 * under 30 days, privacy blockers strip them, and a direct app-domain invite
 * link never passes through the marketing site at all. Without this, the code
 * sits unused in the URL because `withReferralCode` only reads the cookie.
 *
 * Existing cookie values are preserved; only keys present in the URL are written
 * (last-touch). Runs at app bootstrap, before any OAuth redirect drops the URL
 * params, so the cookie is the reliable carrier by the time signup completes.
 */
export const captureAttributionFromUrl = (): void => {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const fromUrl: FunnelFightersAttribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key)
    if (isValidStringValue(value)) {
      fromUrl[key] = value
    }
  }
  if (Object.keys(fromUrl).length === 0) return

  const merged = { ...readAttributionCookie(), ...fromUrl }
  const cookieParts = [
    `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(merged))}`,
    `Max-Age=${COOKIE_MAX_AGE_DAYS * 24 * 60 * 60}`,
    'Path=/',
    'SameSite=Lax',
  ]
  // Match the marketing site's scope so this UPDATES the shared cookie rather
  // than creating a second same-named cookie on the app host.
  if (isProductionHost()) {
    cookieParts.push(`Domain=${COOKIE_DOMAIN}`)
    cookieParts.push('Secure')
  }
  document.cookie = cookieParts.join('; ')
}
